/**
 * Pure helpers for the CascadePolicyEditor — kept out of the component so the
 * load / validate / serialize logic is unit-testable without a DOM. The editor
 * works in the SERIALIZED form (flags as arrays) since it is JSON-native and
 * easy to hold in React state; it reuses @dfp/policy-engine for the actual
 * parse + validation so the client and the server agree on what is valid.
 */
import {
  TZ_DEFAULT_CASCADE_SET,
  parseCascadePolicySet,
  serializeCascadePolicySet,
  validateCascadePolicy,
  type CascadeDesiredState,
  type CascadeFlag,
  type CascadeStage,
  type SerializedCascadePolicy,
  type SerializedCascadePolicySet,
  type SerializedCascadeStep,
} from "@dfp/policy-engine";

export const FREQUENCIES = ["daily", "weekly", "monthly", "fallback"] as const;
export type EditorFrequency = (typeof FREQUENCIES)[number];

export const ALL_FLAGS: readonly CascadeFlag[] = [
  "notify_customer",
  "alert_operator",
  "open_dispute",
  "flag_for_repossession",
];
export const ALL_DESIRED: readonly CascadeDesiredState[] = [
  "UNLOCKED",
  "SOFT_LOCKED",
  "HARD_LOCKED",
];
export const ALL_STAGES: readonly CascadeStage[] = [0, 1, 2, 3, 4, 5, 6, 7];

/**
 * Parse the stored setting value into an editable serialized set. Falls back to
 * the TZ default (serialized) when the value is empty or invalid, so the editor
 * always opens on a valid base. Returns a deep-mutable plain-object copy.
 */
export function loadEditorSet(value: string): SerializedCascadePolicySet {
  const base = parseCascadePolicySet(value) ?? TZ_DEFAULT_CASCADE_SET;
  return JSON.parse(JSON.stringify(serializeCascadePolicySet(base))) as SerializedCascadePolicySet;
}

/** Select a policy from the editor set by editor frequency. */
export function policyOf(
  set: SerializedCascadePolicySet,
  freq: EditorFrequency,
): SerializedCascadePolicy {
  return freq === "fallback" ? set.fallback : set.byFrequency[freq];
}

/** Replace one frequency's policy, returning a new set (immutable update). */
export function withPolicy(
  set: SerializedCascadePolicySet,
  freq: EditorFrequency,
  policy: SerializedCascadePolicy,
): SerializedCascadePolicySet {
  if (freq === "fallback") return { ...set, fallback: policy };
  return { ...set, byFrequency: { ...set.byFrequency, [freq]: policy } };
}

/**
 * Validate one policy for the editor. Returns a friendly message (the invariant
 * that failed) or null when valid. Stage/desired/flag ranges are constrained by
 * the editor's controls, so the only runtime failures are the structural
 * invariants (first step at hour 0, strictly ascending).
 */
export function validatePolicyForEditor(policy: SerializedCascadePolicy): string | null {
  try {
    validateCascadePolicy({
      version: policy.version,
      steps: policy.steps.map((s) => ({
        fromOverdueHours: s.fromOverdueHours,
        stage: s.stage,
        desired: s.desired,
        ...(s.flags ? { flags: new Set(s.flags) } : {}),
      })),
    });
    return null;
  } catch (e) {
    return e instanceof Error
      ? e.message.replace(/^invalid cascade policy: /, "")
      : "invalid policy";
  }
}

/** Per-frequency validation messages; the editor disables save while any is set. */
export function setErrors(set: SerializedCascadePolicySet): Record<EditorFrequency, string | null> {
  return {
    daily: validatePolicyForEditor(set.byFrequency.daily),
    weekly: validatePolicyForEditor(set.byFrequency.weekly),
    monthly: validatePolicyForEditor(set.byFrequency.monthly),
    fallback: validatePolicyForEditor(set.fallback),
  };
}

export function hasAnyError(errs: Record<EditorFrequency, string | null>): boolean {
  return Object.values(errs).some((e) => e !== null);
}

/**
 * Serialize the editor set back to the canonical JSON string for saving, or null
 * when it does not pass the full policy-engine validation. The server re-validates
 * via coerce; this guard means the editor never PUTs a knowingly-invalid value.
 */
export function serializeEditorSet(set: SerializedCascadePolicySet): string | null {
  const parsed = parseCascadePolicySet(set);
  return parsed ? JSON.stringify(serializeCascadePolicySet(parsed)) : null;
}

/** A fresh step to append (hour 0, clean) — the operator then edits it. */
export function blankStep(): SerializedCascadeStep {
  return { fromOverdueHours: 0, stage: 0, desired: "UNLOCKED" };
}

/** Sort a policy's steps ascending by fromOverdueHours (the "tidy" action). */
export function sortedSteps(
  steps: readonly SerializedCascadeStep[],
): readonly SerializedCascadeStep[] {
  return [...steps].sort((a, b) => a.fromOverdueHours - b.fromOverdueHours);
}
